import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type { ContactList, PatchedContact } from '../../../../../js/api';
import { HealthcareEntitiesService } from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { setFormErrorsFromApiErrors } from '../../../../../js/utils/forms';
import { Button } from '../../../components/button';
import { DrawerCloseButton } from '../../../components/drawer';
import { DrawerField, DrawerSectionHeader } from '../../../components/drawer-components';
import { Field, Label } from '../../../components/fieldset';
import { Heading } from '../../../components/heading';
import { Input } from '../../../components/input';
import { useActingAsEntity } from '../../../hooks/useActingAsEntity';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { formatContactName, formatContactPhone } from './contactDisplay';
import { ContactFacilitiesSection } from './ContactFacilitiesSection';
import { CONTACT_UPDATE_PERMISSION, LINK_CREATE_PERMISSION } from './contactPermissions';
import { LinkContactsDialog } from './LinkContactsDialog';
import { contactQueryKeys, invalidateContactQueries } from './queryKeys';

const contactSchema = z.object({
  first_name: z.string().trim().min(1, 'First name is required'),
  last_name: z.string().trim().min(1, 'Last name is required'),
  email: z.string().trim().email('Enter a valid email'),
  phone_number: z.string().trim(),
  phone_number_extension: z.string().trim(),
});

type ContactFormData = z.infer<typeof contactSchema>;

const toFormValues = (contact: ContactList): ContactFormData => ({
  first_name: contact.first_name || '',
  last_name: contact.last_name || '',
  email: contact.email || '',
  phone_number: contact.phone_number || '',
  phone_number_extension: contact.phone_number_extension || '',
});

const FIELDS: { name: keyof ContactFormData; label: string; type?: string; required?: boolean }[] =
  [
    { name: 'first_name', label: 'First Name', required: true },
    { name: 'last_name', label: 'Last Name', required: true },
    { name: 'email', label: 'Email', type: 'email', required: true },
    { name: 'phone_number', label: 'Phone', type: 'tel' },
    { name: 'phone_number_extension', label: 'Extension' },
  ];

const ContactDetailsForm: React.FC<{
  contact: ContactList & { id: string };
  onDone: () => void;
}> = ({ contact, onDone }) => {
  const queryClient = useQueryClient();
  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: toFormValues(contact),
  });
  const { errors, isSubmitting } = form.formState;

  const onSubmit = async (data: ContactFormData) => {
    try {
      await HealthcareEntitiesService.healthcareEntitiesContactsPartialUpdate({
        id: contact.id,
        requestBody: data as PatchedContact,
      });
      toast.success('Contact updated');
      await invalidateContactQueries(queryClient);
      onDone();
    } catch (error) {
      setFormErrorsFromApiErrors(
        error as Parameters<typeof setFormErrorsFromApiErrors>[0],
        form.setError as Parameters<typeof setFormErrorsFromApiErrors>[1]
      );
      handleApiError(error, { messagePrefix: 'Failed to update contact' });
    }
  };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="tw:space-y-3"
      data-testid="contact-details-form"
    >
      <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:@xl:grid-cols-2">
        {FIELDS.map(({ name, label, type, required }) => (
          <Field key={name} className="tw:min-w-0">
            <Label htmlFor={`contact-${name}`}>
              {label}
              {required && <span className="tw:text-stoplight-danger">*</span>}
            </Label>
            <Input
              id={`contact-${name}`}
              type={type}
              {...form.register(name)}
              data-invalid={errors[name] ? true : undefined}
            />
            {errors[name] && (
              <p className="tw:mt-1 tw:text-sm tw:text-stoplight-danger">{errors[name]?.message}</p>
            )}
          </Field>
        ))}
      </div>
      {errors.root?.non_field_errors && (
        <p className="tw:text-sm tw:text-stoplight-danger">
          {errors.root.non_field_errors.message}
        </p>
      )}
      <div className="tw:flex tw:justify-end tw:gap-2">
        <Button plain onClick={onDone} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" color="brandgreen1" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </form>
  );
};

const ACTING_AS_ORG_HINT = "Act as this contact's organization to add facilities";

export const ContactDrawer: React.FC<{
  contactId: string;
  onClose?: () => void;
  showOrganization?: boolean;
}> = ({ contactId, onClose, showOrganization = false }) => {
  const canUpdate = useHasPermission(CONTACT_UPDATE_PERMISSION);
  const canAddLinks = useHasPermission(LINK_CREATE_PERMISSION);
  const { actingAsEntity } = useActingAsEntity();
  const [isEditing, setIsEditing] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);

  useEffect(() => {
    setIsEditing(false);
  }, [contactId]);

  const { data: contact, isLoading } = useQuery({
    queryKey: contactQueryKeys.detail(contactId),
    queryFn: () => HealthcareEntitiesService.healthcareEntitiesContactsRetrieve({ id: contactId }),
  });

  if (isLoading || !contact) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <div className="tw:text-zinc-500">Loading contact...</div>
      </div>
    );
  }

  const contactRow = { ...contact, id: contact.id ?? contactId };
  // Linking creates rows in the contact's org, so staff must act as that org.
  const canAddHere = actingAsEntity?.id === contact.healthcare_entity;

  return (
    <div
      className="tw:@container tw:flex tw:flex-col tw:gap-6 tw:mt-6"
      data-testid="contact-drawer"
    >
      <div className="tw:flex tw:flex-col tw:gap-1">
        <div className="tw:flex tw:justify-between tw:items-center">
          <Heading level={1} className="tw:!text-brand-green-0 tw:!mb-0">
            {formatContactName(contact)}
          </Heading>
          {onClose && <DrawerCloseButton onClick={onClose} />}
        </div>
        {showOrganization && contact.healthcare_entity_name && (
          <div className="tw:text-sm tw:text-zinc-500">
            <span className="tw:truncate tw:italic">{contact.healthcare_entity_name}</span>
          </div>
        )}
      </div>

      <section>
        <DrawerSectionHeader
          text="Contact Details"
          icon={<i className="fal fa-fw fa-address-card tw:text-brand-green-0" />}
          rightElement={
            <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-2">
              {canAddLinks && (
                <Button
                  plain
                  small
                  onClick={() => setIsAddOpen(true)}
                  disabled={!canAddHere}
                  title={canAddHere ? undefined : ACTING_AS_ORG_HINT}
                  data-testid="contact-links-add"
                >
                  <i className="far fa-plus tw:mr-1" />
                  Add to facilities
                </Button>
              )}
              {canUpdate && !isEditing && (
                <Button plain small onClick={() => setIsEditing(true)} data-testid="contact-edit">
                  <i className="far fa-pen tw:mr-1" />
                  Edit
                </Button>
              )}
            </div>
          }
        />
        {isEditing ? (
          <ContactDetailsForm contact={contactRow} onDone={() => setIsEditing(false)} />
        ) : (
          <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-4 tw:gap-y-1">
            <DrawerField label="Name" value={formatContactName(contact)} />
            <DrawerField label="Email" value={contact.email} />
            <DrawerField label="Phone" value={formatContactPhone(contact)} />
          </div>
        )}
      </section>

      <ContactFacilitiesSection
        contactId={contactRow.id}
        contactEntityId={contact.healthcare_entity}
      />
      <LinkContactsDialog
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        mode="add"
        direction="contactsToFacilities"
        contactIds={[contactRow.id]}
        subjectName={formatContactName(contact)}
        healthcareEntityId={contact.healthcare_entity}
      />
    </div>
  );
};
